import { attach, createEffect, createEvent, createStore, sample } from 'effector'
import { not } from 'patronum'
import UAParser from 'ua-parser-js'
import { z } from 'zod'
import { zfd } from 'zod-form-data'

import { logger } from '@portal/logger'

import {
    clientCrashed,
    clientCrashedV2,
    crashReported,
    crashReportFailed,
    type SendCrashlogParams,
    ticketLogSubmit,
} from '@/entities/crashlog'
import { $gameClient, $gameConnectConfig, type GameClient } from '@/entities/game'
import { $portalProfile, $portalProfilePublicID } from '@/entities/profile/portal'
import { zendeskAPI } from '@/shared/api'
import type { TicketSubmitRequest } from '@/shared/api/zendesk/types'
import { deflateRawToBase64 } from '@/shared/lib/compress'
import { createFormValidation } from '@/shared/lib/factory'
import { $lang } from '@/shared/lib/i18n'
import { isNotNull } from '@/shared/lib/types'
import { type NotificationArgs, showNotificationFx } from '@/shared/ui'
import { createHideFx } from '@/shared/ui/modal'

import type { SupportFormValues } from './types'

export const showFx = createEffect<{ defaultNick?: string; defaultEmail?: string } | void, string>()
export const hideFx = createHideFx(showFx)

export const formSubmitted = createEvent<FormData>()
export const formTouched = createEvent()

// ------------- Form validation

const SupportFormValuesSchema = zfd.formData({
    email: zfd.text(z.email()),
    nick: zfd.text(z.string().trim().min(1)),
    description: zfd.text(z.string().trim().min(1)),
})

const { formValidationSucceeded, formValidationFailed } = createFormValidation({
    formSubmitted,
    validationSchema: SupportFormValuesSchema,
    mapError: (_i18n, error) => {
        const invalidFields = new Set(error.issues.flatMap((issue) => issue.path))
        return { invalidFields }
    },
})

function $fieldError(field: string) {
    return createStore(false)
        .on(formValidationFailed, (_, { invalidFields }) => invalidFields.has(field))
        .reset(formSubmitted, formTouched, hideFx)
}

export const $isEmailError = $fieldError('email')
export const $isNickError = $fieldError('nick')
export const $isDescriptionError = $fieldError('description')

// ------------- Ticket submission

// The client's message may carry diagnostic lines under the title; the ticket gets the title alone.
export function crashTitle(errorMessage: string): string | undefined {
    return errorMessage.split(/\r?\n/u)[0] || undefined
}

// The crashlog the ticket links to, if any. Id and title come from one response, so
// they never disagree; the rest (compressed client log) is dropped to free memory.
const $linkedCrash = createStore<{ id: string; title?: string } | null>(null)
    .on(crashReported, (_, { reportId, message }) => ({ id: reportId, title: crashTitle(message) }))
    // nothing to link after a rejected crash; a re-login must not carry the old crash over
    .reset(crashReportFailed, $portalProfilePublicID)

const ticketSubmitFx = attach({
    effect: zendeskAPI.ticketSubmit,
    source: {
        portalProfile: $portalProfile,
        connectConfig: $gameConnectConfig,
        lang: $lang,
        linkedCrash: $linkedCrash,
    },
    mapParams: (
        formValues: SupportFormValues,
        { portalProfile, connectConfig, lang, linkedCrash }
    ): TicketSubmitRequest => ({
        description: formValues.description,
        refferer: window.location.href,
        lang: lang,
        public_id: portalProfile?.publicId,
        platform_id: connectConfig?.platformId ?? portalProfile?.platformId,
        platform_type: connectConfig?.platformType ?? portalProfile?.platformType ?? 'portal',
        nick: formValues.nick,
        email: formValues.email,
        crash_id: linkedCrash?.id,
        crash_title: linkedCrash?.title,
    }),
})

export const $interactionDisabled = ticketSubmitFx.pending

sample({
    clock: formValidationSucceeded,
    target: ticketSubmitFx,
})

sample({
    clock: ticketSubmitFx.done,
    fn: (): NotificationArgs => ({ notification: 'support_message_sent' }),
    target: showNotificationFx,
})

sample({
    clock: ticketSubmitFx.done,
    target: hideFx,
})

sample({
    clock: ticketSubmitFx.fail,
    fn: (): NotificationArgs => ({ notification: 'error_default' }),
    target: showNotificationFx,
})

// ------------- Client log

const collectClientLogFx = createEffect(async (gameClient: GameClient): Promise<SendCrashlogParams> => {
    const userAgent = new UAParser().getResult()

    return {
        crashInfo: JSON.stringify({
            // Keep the client crashlog shape for crashdb indexing.
            error: { type: 'Portal|Support|Ticket' },
            env: { browser: userAgent.browser.name },
            userAgent,
            client: gameClient.getClientInfo(),
            performance: gameClient.getPerformanceStats(),
            network: gameClient.getNetworkStats(),
        }),
        log: await deflateRawToBase64(gameClient.getClientLog()),
        message: 'Support ticket',
        tags: ['info', 'support-ticket'].join(','),
    }
})

// Mirrors $linkedCrash above: both must agree on whether the ticket already has a crash
// report to point at, so they reset on the same signals.
const $clientCrashed = createStore(false)
    .on([clientCrashed, clientCrashedV2], () => true)
    // nothing got through to crashdb; a re-login must not carry the old crash over either
    .reset(crashReportFailed, $portalProfilePublicID)

sample({
    clock: sample({
        // Deliberately on submit, not on validation success: the client log is worth more
        // than the noise from a player who mistyped a field and never got the ticket through.
        clock: formSubmitted,
        // Nothing to collect after a crash: the ticket links that report by crash_id instead.
        filter: not($clientCrashed),
    }),
    // Null outside the play page, which is the only place a client log exists.
    source: $gameClient,
    filter: isNotNull,
    target: collectClientLogFx,
})

sample({
    clock: collectClientLogFx.doneData,
    target: ticketLogSubmit,
})

// Collection failure must not block the support ticket.
sample({
    clock: collectClientLogFx.failData,
    target: createEffect((error: Error) => {
        logger.error('Failed to collect the client log for support ticket', error)
    }),
})
